import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  Stack,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  CreditCard as CreditCardIcon,
  Security as SecurityIcon,
  Update as UpdateIcon,
} from '@mui/icons-material';
import AddPaymentMethodForm from '../../components/AddPaymentMethodForm';
import PlanSelector from '../../components/PlanSelector';

const OnboardingPaymentSubscription: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0); // 0 = payment, 1 = subscription
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentMethodAdded, setPaymentMethodAdded] = useState(false);
  const [subscriptionSelected, setSubscriptionSelected] = useState(false);
  const [error, setError] = useState('');

  const steps = ['Payment Method', 'Select Plan'];

  const handlePaymentSuccess = () => {
    setPaymentMethodAdded(true);
    setShowPaymentForm(false);
  };

  const handleContinueToSubscription = () => {
    if (!paymentMethodAdded) {
      setError('Please add a payment method to continue');
      return;
    }
    setActiveStep(1);
  };

  const handlePlanChanged = async () => {
    setSubscriptionSelected(true);
    setTimeout(() => {
      navigate('/app');
    }, 1500);
  };

  const handleBack = () => {
    if (activeStep === 1) {
      setActiveStep(0);
    } else {
      navigate('/onboarding/organisation');
    }
  };

  const handleSkip = () => {
    navigate('/app');
  };

  return (
    <Box sx={{ maxWidth: activeStep === 0 ? 600 : 900, mx: 'auto', py: 3 }}>
      {/* Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Payment Method Step */}
      {activeStep === 0 && (
        <>
          <Box mb={4} textAlign="center">
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Add Payment Method
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Securely add your payment details. You won't be charged until you select a
              subscription plan.
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {paymentMethodAdded && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Payment method added successfully!
            </Alert>
          )}

          {!showPaymentForm && !paymentMethodAdded && (
            <Paper variant="outlined" sx={{ mb: 3, p: 3 }}>
              <Typography variant="subtitle1" fontWeight={500} gutterBottom>
                Why add a payment method now?
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <SecurityIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Secure & Encrypted"
                    secondary="Your payment information is encrypted and secure with Stripe"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CreditCardIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="No Immediate Charges"
                    secondary="You won't be charged until you select and confirm a subscription"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <UpdateIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Change Anytime"
                    secondary="Update or remove your payment method at any time from settings"
                  />
                </ListItem>
              </List>
            </Paper>
          )}

          {showPaymentForm && (
            <AddPaymentMethodForm
              isFirstCard={true}
              onSuccess={handlePaymentSuccess}
              onCancel={() => setShowPaymentForm(false)}
            />
          )}

          <Stack spacing={2}>
            {!showPaymentForm && !paymentMethodAdded && (
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<CreditCardIcon />}
                onClick={() => setShowPaymentForm(true)}
              >
                Add Payment Method
              </Button>
            )}

            {paymentMethodAdded && (
              <Button
                variant="contained"
                size="large"
                fullWidth
                endIcon={<ArrowForwardIcon />}
                onClick={handleContinueToSubscription}
              >
                Continue to Plan
              </Button>
            )}

            <Stack direction="row" spacing={2} justifyContent="space-between">
              <Button variant="text" startIcon={<ArrowBackIcon />} onClick={handleBack}>
                Back
              </Button>
              <Button variant="text" onClick={handleSkip} color="inherit">
                Skip for now
              </Button>
            </Stack>
          </Stack>
        </>
      )}

      {/* Plan Selection Step */}
      {activeStep === 1 && (
        <>
          <Box mb={4} textAlign="center">
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Choose Your Plan
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Select the plan that best fits your R&D management needs. You can change your plan at
              any time.
            </Typography>
          </Box>

          {subscriptionSelected && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Plan purchased successfully! Redirecting to welcome...
            </Alert>
          )}

          <Box sx={{ mb: 4 }}>
            <PlanSelector
              currentSubscription={null}
              onPlanChanged={handlePlanChanged}
              onPaymentMethodRequired={() => setActiveStep(0)}
              disabled={subscriptionSelected}
              showCurrentPlanChip={false}
            />
          </Box>

          <Stack spacing={2}>
            <Stack direction="row" spacing={2} justifyContent="space-between">
              <Button
                variant="text"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                disabled={subscriptionSelected}
              >
                Back
              </Button>
              <Button
                variant="text"
                onClick={handleSkip}
                color="inherit"
                disabled={subscriptionSelected}
              >
                Skip for now
              </Button>
            </Stack>
          </Stack>
        </>
      )}
    </Box>
  );
};

export default OnboardingPaymentSubscription;
